/**
 * 扣柜原因 —— 柜子「为什么动不了」那几个标签。
 *
 * 原先只长在柜子监控弹窗里。派单/柜子管理列表的 On Hold 那一行也要照原话显示
 * 同一句（状态只说了「动不了」，没说是海关、船司还是欠费），所以搬到共享层，
 * 两处用同一套措辞和同一套颜色 —— 否则同一只柜在两个界面上会有两种说法。
 *
 * 措辞表和颜色表在 utils/holdReasons.ts。
 */
import React from 'react'
import type { ContainerHoldDetail } from '../../types/scraper'
import {
  formatHolds,
  describeHoldDetail,
  holdCategoryStyle,
  shortHoldChips,
} from '../../utils/holdReasons'

/**
 * The Holds cell. A container is regularly blocked for several reasons at
 * once, and the three terminals word them differently, so show the terminal's
 * own label rather than translating everything into one tag.
 *
 * Rows written before hold details existed only have the tag array plus the
 * legacy single terminal-code string — fall back to those so old monitors
 * don't render as blank.
 */
export const HoldReasons: React.FC<{
  details: ContainerHoldDetail[] | null
  holds: string[] | null
  terminalHoldCodes?: string | null
  max?: number
  /** 追加到悬浮说明末尾的一两行（列表那边用来写「什么时候问到的」）。 */
  footnote?: string
  /** 外层容器的额外 class，列表里用来收窄宽度。 */
  className?: string
  /**
   * 窄栏模式：显示短说法（`Not available` 而不是 `NOT AVAILABLE FOR PICKUP`）、
   * 重复的合并、字号和内边距都收一档。
   *
   * 给列表那一列用 —— 它只有 130px 宽，放码头原话的结果是每条都被切一半、
   * 一行柜子占三行高，切到一半的句子还不如一个短词说得清。完整原话没有丢，
   * 鼠标停上去就是。柜子监控弹窗**不**开这个模式：那一列宽，而调度员在那边
   * 要的恰恰是码头自己的原话。
   */
  compact?: boolean
}> = ({ details, holds, terminalHoldCodes, max = 2, footnote, className, compact }) => {
  if (!details || details.length === 0) {
    const legacy = formatHolds(holds)
    if (legacy === '-') return <span className="text-slate-400">-</span>
    const legacyTitle = [terminalHoldCodes, footnote].filter(Boolean).join('\n') || undefined
    return (
      <span className="text-slate-600" title={legacyTitle}>
        {legacy}
        {terminalHoldCodes ? ` (${terminalHoldCodes})` : ''}
      </span>
    )
  }

  // 窄栏模式下先缩短再去重，所以 `+N` 数的是**合并后**还剩几条 —— 数合并前的
  // 会凭空多出几个「还有 2 条」，点开一看是同一句话。
  const chips = compact
    ? shortHoldChips(details)
    : details.map((d, i) => ({ label: d.label, category: d.category, key: `${d.category}-${d.code ?? i}` }))
  const shown = chips.slice(0, max)
  const hidden = chips.length - shown.length
  const reasonText = details.map(describeHoldDetail).join('\n')
  const fullText = footnote ? `${reasonText}\n${footnote}` : reasonText

  return (
    <div
      className={`flex flex-wrap items-center ${compact ? 'gap-0.5' : 'gap-1'} ${className ?? 'max-w-[220px]'}`}
      title={fullText}
    >
      {shown.map((c, i) => (
        <span
          key={`${c.category}-${c.label}-${i}`}
          className={`inline-flex items-center rounded border font-medium whitespace-nowrap max-w-full truncate ${
            compact ? 'px-1 py-0 text-[9px] leading-[13px]' : 'px-1.5 py-0.5 text-[10px]'
          } ${holdCategoryStyle[c.category] || 'bg-slate-50 text-slate-600 border-slate-200'}`}
        >
          {c.label}
        </span>
      ))}
      {hidden > 0 && (
        <span className={`${compact ? 'text-[9px]' : 'text-[10px]'} text-slate-500 font-medium`}>+{hidden}</span>
      )}
    </div>
  )
}
